<script setup lang="ts">
    import { getConfig, klinikenApi } from "@/api";
    import ButtonLink from "@/components/ButtonLink.vue";
    import ModalContent from "@/components/generic/ModalContent.vue";
    import { flashWarning } from "@/utils";
    import { computed, onBeforeMount, ref, toRaw, watch } from "vue";

    // TODO Bryt ut till backend och frontendversioner
    interface Komplikation {
        id: number;
        code: string;
        tidpunkt: string;
        kapitelnamn: string;
        originalText: string;
        kategori?: string;
        svarighetsgrad?: number;
        kod?: number;
    }

    interface Svarighetsgrad {
        id: number;
        code: string;
        displayName: string;
    }

    interface Option {
        value: number;
        label: string;
    }

    interface KategoriOption extends Omit<Option, "value"> {
        value: string;
    }

    defineProps<{
        name: string;
        readonly: boolean;
        required?: boolean;
    }>();

    const defaultGradCode = "Grad 0";
    const defaultCode = "U0";
    const defaultKategori = "Normalförlopp";

    /**
     * Datan kopplad till v-model för input type group
     */
    const formData = ref<Komplikation[]>([]);
    const computedFormData = computed(() => structuredClone(toRaw(formData.value)));
    /**
     * Samling av alla komplikationer hämtade från BE
     */
    const komplikationer = ref<Komplikation[]>([]);
    /**
     * Samling av alla svårighetsgrader hämtade från BE
     */
    const svarighetsgrader = ref<Svarighetsgrad[]>([]);
    /**
     * Ska modalen med information om svårighetsgrader visas?
     */
    const showSvarighetsgradModal = ref(false);
    /**
     * Samling av komplikationer konverterade till options som ska visas i en select
     */
    const komplikationerOptions = computed<Map<string, Option[]>>(() => {
        if (!komplikationer.value) {
            return new Map();
        }

        return new Map(
            komplikationer.value.reduce((options, komplikation) => {
                const modifiedMap: Map<string, Option[]> = new Map(options as Map<string, Option[]>);
                const komplikationer = modifiedMap.get(komplikation.kapitelnamn);

                modifiedMap.set(komplikation.kapitelnamn, [
                    ...(komplikationer ? komplikationer : []),
                    {
                        value: komplikation.id,
                        label: komplikation.originalText,
                    },
                ]);

                return modifiedMap;
            }, new Map() as Map<string, Option[]>)
        );
    });
    /**
     * Samling av kategorier konverterade till options som ska visas i en select
     */
    const kategorierOptions = computed<KategoriOption[]>(() => {
        if (!komplikationerOptions.value) {
            return [];
        }

        return [...komplikationerOptions.value.keys()].map((kategori) => ({
            value: kategori,
            label: kategori,
        }));
    });

    /**
     * Samling av svårighetsgrader konverterade till options som ska visas i en select
     */
    const svarighetsgraderOptions = computed<Option[]>(() => {
        if (!svarighetsgrader?.value) {
            return [];
        }

        return svarighetsgrader.value.map((grad) => ({
            value: grad.id,
            label: grad.code,
        }));
    });

    /**
     * Hämtning av komplikationer från BE och lagring i ref
     */

    const retrievekomplikationer = async () => {
        const response = await klinikenApi.get<Komplikation[]>(
            "operation/kodverk/postopkomplikationer/",
            getConfig({})
        );

        komplikationer.value = response.data as Komplikation[];
    };

    /**
     * Hämtning av svårighetsgrader från BE och lagring i ref
     */

    const retrieveSvarighetsgrader = async () => {
        const response = await klinikenApi.get<{
            results: Svarighetsgrad[];
        }>("operation/kodverk/postopkomplikationsvarighetsgrader/", getConfig({}));

        svarighetsgrader.value = response.data.results;
    };

    /**
     * Lyssnare som triggras när option valts i selecten för kategori.
     * Om en specifik kategori valts så ska automatiskt andra fält fyllas i
     */

    const onKategoriSelected: (props: { index: number; option: KategoriOption }) => void = ({ index, option }) => {
        if (option?.value === defaultKategori) {
            const newFormData = [...formData.value];

            newFormData[index] = {
                ...newFormData[index],
                kod: komplikationer.value.find((komplikation) => komplikation.code === defaultCode)?.id,
                svarighetsgrad: svarighetsgrader.value.find((grad) => grad.code === defaultGradCode)?.id,
            };

            formData.value = newFormData;
        }
    };

    /**
     * Lyssnare som triggras av input på selecten för kategori.
     * Om värdet tömts så ska automatiskt andra fält tömmas.
     */
    const onKategoriCleared: (props: { index: number; value: string }) => void = ({ index, value }) => {
        if (!value) {
            const newFormData = [...formData.value];

            if (newFormData.length === 1) {
                newFormData[index] = getDefaultValue();
            } else {
                newFormData[index] = {} as Komplikation;
            }

            formData.value = newFormData;
        }
    };

    /**
     * Hämtning av options för komplikationer baserat på kategori
     */
    const getKomplikationerOptions: (props: { index: number }) => Option[] = ({ index }) => {
        if (!formData?.value?.[index]?.kategori) {
            return [];
        }

        return komplikationerOptions.value.get(formData.value[index].kategori as string) ?? [];
    };

    /**
     * Hämtning av options för svårighetsgrader baserat på kategori.
     * Filtrering av alternativ görs baserat på kategori
     */

    const getSvarighetsgraderOptions: (props: { index: number }) => Option[] = ({ index }) => {
        if (!formData?.value?.[index]?.kategori) {
            return [];
        }

        const komplikation = formData.value[index];

        if (komplikation.kategori === defaultKategori) {
            const foundGradOption = svarighetsgraderOptions.value.find((grad) => grad.label === defaultGradCode);

            return foundGradOption ? [foundGradOption] : [];
        }

        return svarighetsgraderOptions.value.filter((grad) => grad.label !== defaultGradCode);
    };

    /**
     * Hämta label att skriva ut i select för svårighetsgrad.
     * Om option som skickas inte är ett objekt (t.ex om det kommer in från backend) så hämtar vi text från
     * svårighetsgrad för utskrift
     */
    const getSvarighetsgradOptionLabel: (option: string | number | Option) => string = (option) => {
        if (typeof option === "string" || typeof option === "number") {
            return svarighetsgrader.value.find(({ id }) => Number(id) === Number(option))?.code ?? "";
        }

        return option?.label ?? "";
    };

    /**
     * Hämta label att skriva ut i select för komplikation.
     * Om option som skickas inte är ett objekt (t.ex om det kommer in från backend) så hämtar vi text från
     * komplikationen för utskrift
     */
    const getKomplikationOptionLabel: (option: string | number | Option) => string = (option) => {
        if (typeof option === "string" || typeof option === "number") {
            return komplikationer.value.find(({ id }) => Number(id) === Number(option))?.originalText ?? "";
        }

        return option?.label ?? "";
    };

    const getDefaultValue = () => {
        return {
            kategori: defaultKategori,
            kod: komplikationer.value.find((komplikation) => komplikation.code === defaultCode)?.id,
            svarighetsgrad: svarighetsgrader.value.find((grad) => grad.code === defaultGradCode)?.id,
        } as Komplikation;
    };

    onBeforeMount(async () => {
        try {
            await retrievekomplikationer();
            await retrieveSvarighetsgrader();
        } catch (error) {
            console.error(error);

            flashWarning("Kunde inte hämta postopkomplikationer");
        }
    });

    watch(
        computedFormData,
        (newFormData, oldFormData) => {
            if (JSON.stringify(newFormData) === JSON.stringify(oldFormData) || oldFormData.length === 0) {
                return;
            }

            // Samling av alla komplikationer som har fått ändrad kategori i nya datan
            const newKategories: number[] = [];

            newFormData.forEach((komplikation, index) => {
                const { kategori: oldKategori, ...oldData } = oldFormData[index] ?? {};
                const { kategori: newKategori, ...newData } = komplikation;

                // Kollar om kategorin förändrats på raden
                if (oldKategori && newKategori !== oldKategori && JSON.stringify(newData) === JSON.stringify(oldData)) {
                    newKategories.push(index);
                }
            });

            if (newKategories.length > 0) {
                const updatedFormData = [...newFormData];

                newKategories.forEach((komplikationIndex) => {
                    const komplikation = updatedFormData[komplikationIndex];

                    // Sätt till default om vald kategori är defaultkategori
                    updatedFormData[komplikationIndex] =
                        komplikation.kategori === defaultKategori
                            ? getDefaultValue()
                            : ({
                                  kategori: komplikation.kategori,
                              } as Komplikation);
                });

                // Ser till att data alltid är en array med default
                if (newFormData.length === 1 && Object.keys(newFormData[0]).length === 0) {
                    formData.value = [getDefaultValue()];
                } else {
                    formData.value = updatedFormData;
                }
            }
        },
        { deep: true }
    );
</script>

<template>
    <div class="postopavvikelse-group">
        <h4>Postoperativ avvikelse</h4>

        <FormulateInput
            :name="name"
            class="group"
            type="group"
            v-model="formData"
            :repeatable="true"
            add-label="Lägg till avvikelse"
            remove-position="after"
            remove-label="Ta bort avvikelse"
            :readonly="readonly"
            :required="required"
        >
            <template #default="{ index }">
                <FormulateInput
                    id="postopavvikelse-kategori"
                    name="kategori"
                    :validation="required ? 'required' : ''"
                    label="Postoperativ avvikelse kategori *"
                    type="vueselect"
                    :options="kategorierOptions"
                    @option:selected="onKategoriSelected({ index, option: $event })"
                    @input="onKategoriCleared({ index, value: $event })"
                />
                <FormulateInput
                    class="kod"
                    id="postopavvikelse-kod"
                    label="Postoperativ avvikelse *"
                    name="kod"
                    :validation="required ? 'required' : ''"
                    type="vueselect"
                    :options="getKomplikationerOptions({ index })"
                    :getOptionLabel="getKomplikationOptionLabel"
                >
                </FormulateInput>

                <FormulateInput
                    class="svarighetsgrad"
                    id="postopavvikelse-svarighetsgrad"
                    name="svarighetsgrad"
                    :validation="required ? 'required' : ''"
                    label="Svårighetsgrad *"
                    type="vueselect"
                    :options="getSvarighetsgraderOptions({ index })"
                    :getOptionLabel="getSvarighetsgradOptionLabel"
                >
                    <ButtonLink type="button" class="forklaring-button" @click="showSvarighetsgradModal = true">
                        Förklaring
                    </ButtonLink>
                </FormulateInput>

                <FormulateInput
                    id="postopavvikelse-tidpunkt"
                    name="tidpunkt"
                    :validation="required ? 'required' : ''"
                    label="Tidpunkt för avvikelse"
                    type="timepicker"
                    :readonly="readonly"
                    useNow
                />
            </template>
        </FormulateInput>
        <portal v-if="showSvarighetsgradModal" to="modal">
            <ModalContent
                @close="showSvarighetsgradModal = false"
                title="Komplikationsgrad förklaring"
                show-close-button
                class="svarighetsgrad-modal"
            >
                <dl class="forklaringar">
                    <template v-for="grad in svarighetsgrader">
                        <div :key="grad.code">
                            <dt>{{ grad.code }}</dt>
                            <dd>{{ grad.displayName }}</dd>
                        </div>
                    </template>
                </dl>
            </ModalContent>
        </portal>
    </div>
</template>

<style scoped lang="scss">
    @import "@/style/form";

    // För att targetta modalen behöver css:en ligga utanför komponentens
    .svarighetsgrad-modal {
        .forklaringar {
            display: grid;
            grid-gap: 22px 42px;

            dt {
                font-family: Roboto Medium, sans-serif;
                margin: 0;
            }

            dd {
                grid-column-start: 2;
                margin: 0;
            }
        }
    }

    .postopavvikelse-group {
        .group {
            position: relative;

            .forklaring-button {
                background-color: transparent;
                border: none;
                cursor: pointer;
                background-image: url("@/assets/link_internal.svg");
                background-position: left;
                background-size: 13px 13px;
                height: auto;
                padding-left: 18px;
                position: relative;
                top: 4px;
            }

            ::v-deep .formulate-input-group {
                flex-flow: column;
                gap: 0 !important;

                .formulate-input-grouping {
                    .formulate-input-group-repeatable {
                        top: unset;
                        display: flex;
                        justify-content: unset;

                        .formulate-input-group-repeatable-remove {
                            min-width: unset;
                            top: unset;
                            align-self: center;
                            width: 100%;
                        }
                    }
                }

                .formulate-input-group-add-more {
                    position: initial;
                    width: fit-content;

                    .formulate-input {
                        margin: 0;
                    }
                }
            }
        }
    }
</style>
